// src/components/PaymentModal.jsx
// MODIFIÉ : Ajout affichage consigne casiers

import React, { useState, useEffect } from 'react';
import './payment-modal.css';
import ApiService from '../services/ApiService';
import { useAuth } from '../contexts/AuthContext';

// Fonction pour formater les prix
const formatPrice = (price) => {
  return price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

const PaymentModal = ({ isOpen, onClose, orderData, onPaymentSuccess }) => {
  const { user } = useAuth();
  
  const [paymentMethod, setPaymentMethod] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [paymentStep, setPaymentStep] = useState('selection');
  const [statusMessage, setStatusMessage] = useState('');
  const [sessionData, setSessionData] = useState(null);
  const [isInitializing, setIsInitializing] = useState(false);
  // ✅ NOUVEAU : Stocker l'ID de précommande retourné par le backend
  const [preorderId, setPreorderId] = useState(null);

  // Réinitialiser l'état quand le modal se ferme
  useEffect(() => {
    if (!isOpen) {
      setPaymentMethod('');
      setPhoneNumber('');
      setError('');
      setPaymentStep('selection');
      setStatusMessage('');
      setSessionData(null);
      setIsInitializing(false);
      setPreorderId(null); // ✅ NOUVEAU : Reset preorderId
    } else if (isOpen && orderData) {
      if (orderData.payment_method) {
        setPaymentMethod(orderData.payment_method);
        if (orderData.payment_method === 'MoMo') {
          setPaymentStep('input');
          setIsInitializing(false);
        } else {
          initializePaymentOnOpen();
        }
      }
    }
  }, [isOpen, orderData]);
  
  // Vérifier que l'utilisateur est connecté
  useEffect(() => {
    if (isOpen && (!user || !user.id)) {
      console.error("❌ PaymentModal : Utilisateur non connecté ou id_utilisateur manquant");
      setError("Vous devez être connecté pour effectuer un paiement");
      setPaymentStep('selection');
    } else if (isOpen && user) {
      console.log("✅ PaymentModal : Utilisateur connecté", {
        id: user.id,
        nom: user.nom,
        prenom: user.prenom,
        telephone: user.telephone
      });
    }
  }, [isOpen, user]);

  // Initialisation Orange Money
  const initializePaymentOnOpen = async () => {
    if (!user || !user.id) {
      setError("Vous devez être connecté pour effectuer un paiement");
      setPaymentStep('selection');
      setIsInitializing(false);
      return;
    }
    
    setIsInitializing(true);
    setError('');
    setStatusMessage('Initialisation du paiement...');

    try {
      console.log("🚀 Initialisation paiement Orange Money", {
        id_utilisateur: user.id,
        id_distributeur: orderData.id_distributeur,
        montant: orderData.montant_total
      });
      
      const initResponse = await fetch(`${ApiService.getBaseUrl()}/payment/?action=initiate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          id_utilisateur: user.id,
          id_distributeur: orderData.id_distributeur,
          amount: orderData.montant_total,
		  consigne_casiers: orderData.consigne_casiers || 0,
          // ✅ NOUVEAU : Données pour le pré-enregistrement
          data_panier: orderData.data_panier || null,
          data_livraison: orderData.data_livraison || null,
          mode_paiement: 'OM'
        })
      });

      const initData = await initResponse.json();
      
      console.log("📥 Réponse initialisation:", initData);

      if (!initData.success) {
        throw new Error(initData.message || 'Échec de l\'initialisation du paiement');
      }

      setSessionData(initData.data);
      setPaymentStep('input');
      setStatusMessage('');
      
      // ✅ NOUVEAU : Stocker l'ID de précommande si retourné
      if (initData.data?.preorder_id) {
        setPreorderId(initData.data.preorder_id);
        console.log("📝 Précommande créée avec ID:", initData.data.preorder_id);
      }
      
      console.log("✅ Paiement initialisé avec succès", {
        session_id: initData.data.session_id,
        pay_token: initData.data.pay_token
      });
      
    } catch (err) {
      console.error('❌ Erreur lors de l\'initialisation:', err);
      setError(`Erreur d'initialisation: ${err.message}`);
      setPaymentStep('selection');
    } finally {
      setIsInitializing(false);
    }
  };

  const handlePaymentMethodSelect = (method) => {
    setPaymentMethod(method);
    setError('');
    if (method === 'MoMo') {
      setPaymentStep('input');
    } else {
      initializePaymentOnOpen();
    }
  };

  const validatePhoneNumber = (number) => {
    const cleanNumber = number.replace(/\s/g, '');
    const regex = /^(6[5-9]\d{7}|2[0-9]\d{7})$/;
    return regex.test(cleanNumber);
  };

  const formatPhoneNumber = (number) => {
    const clean = number.replace(/\D/g, '');
    if (clean.length >= 3) {
      return clean.slice(0, 3) + ' ' + clean.slice(3, 6) + ' ' + clean.slice(6, 9);
    }
    return clean;
  };

  const handlePhoneNumberChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 9) {
      setPhoneNumber(value);
      setError('');
    }
  };

  // === LOGIQUE DE PAIEMENT PRINCIPALE ===
  const executePayment = async () => {
    if (!validatePhoneNumber(phoneNumber)) {
      setError('Numéro de téléphone invalide');
      return;
    }
    
    if (!user || !user.id) {
      setError("Session expirée. Veuillez vous reconnecter.");
      return;
    }

    setIsLoading(true);
    setError('');
    setPaymentStep('processing');
    setStatusMessage('Envoi de la demande de paiement...');

    try {
      let response;
      let data;

      if (paymentMethod === 'OM') {
        if (!sessionData || !sessionData.pay_token) {
          throw new Error('Session de paiement invalide. Veuillez réessayer.');
        }

        console.log("💳 Exécution paiement Orange Money", {
          subscriber_msisdn: phoneNumber,
          session_id: sessionData.session_id
        });

        response = await fetch(`${ApiService.getBaseUrl()}/payment/?action=execute`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          },
          body: JSON.stringify({
            subscriber_msisdn: phoneNumber,
            session_id: sessionData.session_id
          })
        });
        data = await response.json();

        if (!data.success) {
          const msg = data.data?.inittxnmessage || data.message || 'Paiement échoué';
          throw new Error(msg);
        }

        setStatusMessage('En attente de confirmation...\nVeuillez valider le paiement sur votre téléphone');
        checkOrangeStatus(sessionData.pay_token);

      } else if (paymentMethod === 'MoMo') {
        console.log("💳 Exécution paiement MTN Mobile Money", {
          phone: phoneNumber,
          amount: orderData.montant_total,
          order_id: orderData.id_commande,
          id_distributeur: orderData.id_distributeur
        });
        
        response = await fetch(`${ApiService.getBaseUrl()}/mtn/?action=execute`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          },
          body: JSON.stringify({
            phone: phoneNumber,
            amount: orderData.montant_total,
            order_id: orderData.id_commande,
            id_distributeur: orderData.id_distributeur,
			consigne_casiers: orderData.consigne_casiers || 0,
            // ✅ NOUVEAU : Données pour le pré-enregistrement
            id_utilisateur: user.id,
            data_panier: orderData.data_panier || null,
            data_livraison: orderData.data_livraison || null,
            mode_paiement: 'MoMo'
          })
        });
        data = await response.json();

        if (!data.success) {
          throw new Error(data.message || 'Échec de l\'initialisation MTN');
        }

        // ✅ NOUVEAU : Stocker l'ID de précommande si retourné
        if (data.preorder_id) {
          setPreorderId(data.preorder_id);
          console.log("📝 Précommande MTN créée avec ID:", data.preorder_id);
        }

        setStatusMessage('En attente de confirmation...\nVeuillez valider le paiement sur votre téléphone');
        checkMTNStatus(data.reference_id);
      }

    } catch (err) {
      console.error('❌ Erreur paiement:', err);
      setError(err.message);
      setIsLoading(false);
      setPaymentStep('input');
    }
  };

  // === POLLING ORANGE MONEY ===
  const checkOrangeStatus = async (payToken, attempts = 0) => {
    const maxAttempts = 60;
    if (attempts >= maxAttempts) {
      setError('Temps écoulé : veuillez réessayer.');
      setIsLoading(false);
      setPaymentStep('input');
      return;
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      const response = await fetch(`${ApiService.getBaseUrl()}/payment/?action=status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({ pay_token: payToken }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      const data = await response.json();

      if (data.success && data.data) {
        console.log(`📊 Statut paiement Orange (tentative ${attempts + 1}):`, data.data.status);
        
        if (data.data.status === 'SUCCESSFULL') {
          console.log("✅ Paiement Orange réussi !");
          handlePaymentSuccess('OM', payToken, null);
          return;
        } else if (data.data.status === 'FAILED') {
          console.error("❌ Paiement Orange échoué");
          setError('Paiement échoué. Veuillez réessayer.');
          setIsLoading(false);
          setPaymentStep('input');
          return;
        }
      }

      setTimeout(() => checkOrangeStatus(payToken, attempts + 1), 2000);
    } catch (err) {
      console.warn("⚠️ Erreur vérification statut, nouvelle tentative...", err.message);
      setTimeout(() => checkOrangeStatus(payToken, attempts + 1), 2000);
    }
  };

  // === POLLING MTN MOBILE MONEY ===
  const checkMTNStatus = async (referenceId, attempts = 0) => {
    const maxAttempts = 60;
    if (attempts >= maxAttempts) {
      setError('Temps écoulé : veuillez réessayer.');
      setIsLoading(false);
      setPaymentStep('input');
      return;
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      const response = await fetch(`${ApiService.getBaseUrl()}/mtn/?action=status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({ reference_id: referenceId }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      const data = await response.json();

      if (data.success) {
        console.log(`📊 Statut paiement MTN (tentative ${attempts + 1}):`, data.status);
        
        if (data.status === 'SUCCESSFUL') {
          console.log("✅ Paiement MTN réussi !");
          handlePaymentSuccess('MTN', null, referenceId);
          return;
        } else if (data.status === 'FAILED') {
          console.error("❌ Paiement MTN échoué");
          setError('Paiement échoué. Veuillez réessayer.');
          setIsLoading(false);
          setPaymentStep('input');
          return;
        }
      }

      setTimeout(() => checkMTNStatus(referenceId, attempts + 1), 2000);
    } catch (err) {
      console.warn("⚠️ Erreur vérification statut MTN, nouvelle tentative...", err.message);
      setTimeout(() => checkMTNStatus(referenceId, attempts + 1), 2000);
    }
  };

  // Gestion du succès du paiement
  const handlePaymentSuccess = (operator, payToken, referenceId) => {
    setStatusMessage('Paiement réussi !');
    
    console.log("✅ Paiement confirmé", {
      operator,
      pay_token: payToken,
      reference_id: referenceId,
      amount: orderData.montant_total,
      phone: phoneNumber,
      preorder_id: preorderId // ✅ NOUVEAU
    });
    
    setTimeout(() => {
      onPaymentSuccess({
        success: true,
        operator,
        pay_token: payToken,
        reference_id: referenceId,
        amount: orderData.montant_total,
        phone: phoneNumber,
        preorder_id: preorderId // ✅ NOUVEAU : Inclure l'ID de précommande
      });
      onClose();
    }, 1500);
  };

  // ✅ NOUVEAU : Calculer si on a une consigne
  const hasConsigne = orderData?.consigne_casiers && orderData.consigne_casiers > 0;
  const montantProduits = orderData?.montant_produits || (orderData?.montant_total - (orderData?.consigne_casiers || 0));

  if (!isOpen) return null;

  return (
    <div
      className="payment-modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isLoading && !isInitializing) {
          onClose();
        }
      }}
    >
      <div className="payment-modal" onClick={(e) => e.stopPropagation()}>
        <div className="payment-modal-header">
          <h2>Paiement de la commande</h2>
          {!isLoading && !isInitializing && (
            <button className="close-button" onClick={onClose}>×</button>
          )}
        </div>

        <div className="payment-modal-content">
          {/* ✅ MODIFIÉ : Récapitulatif avec affichage consigne */}
          <div className="order-summary">
            <h3>Récapitulatif</h3>
            
            {/* Si consigne présente, afficher le détail */}
            {hasConsigne ? (
              <>
                <div className="summary-line summary-products">
                  <span>Produits</span>
                  <span>{formatPrice(montantProduits)} F</span>
                </div>
                <div className="summary-line summary-consigne">
                  <span>
                    Consigne casiers ({orderData.nb_casiers_consignes || 0})
                    <span className="consigne-hint">remboursable</span>
                  </span>
                  <span>{formatPrice(orderData.consigne_casiers)} F</span>
                </div>
                <div className="summary-line summary-total">
                  <span>Total à payer</span>
                  <span className="amount">{formatPrice(orderData.montant_total)} F</span>
                </div>
              </>
            ) : (
              /* Affichage simple sans consigne */
              <div className="summary-line">
                <span>Commande #{orderData?.id_commande}</span>
                <span className="amount">{formatPrice(orderData?.montant_total)} F</span>
              </div>
            )}
          </div>

          {/* Sélection du mode */}
          {paymentStep === 'selection' && !isInitializing && (
            <div className="payment-methods">
              <h3>Choisissez votre mode de paiement</h3>

              <button
                className="payment-method-button orange-money"
                onClick={() => handlePaymentMethodSelect('OM')}
                disabled={!user || !user.id}
              >
                <img src="/images/payment/orange-money-logo.png" alt="Orange Money" />
                <span>Orange Money</span>
              </button>

              <button
                className="payment-method-button mtn-momo"
                onClick={() => handlePaymentMethodSelect('MoMo')}
                disabled={!user || !user.id}
              >
                <img src="/images/payment/mtn-momo-logo.png" alt="MTN Mobile Money" />
                <span>MTN Mobile Money</span>
              </button>
            </div>
          )}

          {/* Initialisation */}
          {isInitializing && (
            <div className="payment-processing">
              <div className="spinner"></div>
              <p className="status-message">Préparation du paiement...</p>
            </div>
          )}

          {/* Saisie du numéro */}
          {paymentStep === 'input' && !isInitializing && (
            <div className="phone-input-section">
              <button
                className="back-button"
                onClick={() => {
                  setPaymentStep('selection');
                  setSessionData(null);
                  setError('');
                  setPreorderId(null); // ✅ NOUVEAU : Reset preorderId
                }}
              >
                ← Retour
              </button>

              <div className="payment-method-selected">
                <img
                  src={
                    paymentMethod === 'OM'
                      ? '/images/payment/orange-money-logo.png'
                      : '/images/payment/mtn-momo-logo.png'
                  }
                  alt={paymentMethod}
                  className="selected-logo"
                />
                <p className="payment-instruction">
                  Saisissez votre numéro pour payer avec{' '}
                  <strong>{paymentMethod === 'OM' ? 'Orange Money' : 'MTN Mobile Money'}</strong>
                </p>
              </div>

              <div className="phone-input-container">
                <label htmlFor="phone">Numéro de téléphone</label>
                <input
                  type="tel"
                  id="phone"
                  value={formatPhoneNumber(phoneNumber)}
                  onChange={handlePhoneNumberChange}
                  placeholder="6XX XXX XXX"
                  maxLength="11"
                  autoFocus
                />
                {error && (
                  <div className="error-with-action">
                    <p className="error-message">{error}</p>
                    {error.includes('solde') && (
                      <button className="retry-button" onClick={executePayment}>
                        Réessayer
                      </button>
                    )}
                  </div>
                )}
              </div>

              <button
                className="pay-button"
                onClick={executePayment}
                disabled={phoneNumber.length !== 9}
              >
                Payer {formatPrice(orderData?.montant_total)} F
              </button>
            </div>
          )}

          {/* Traitement */}
          {paymentStep === 'processing' && (
            <div className="payment-processing">
              <div className="spinner"></div>
              <p className="status-message">{statusMessage}</p>
              {statusMessage.includes('valider') && (
                <>
                  <div className="phone-display">
                    <span className="phone-icon">📱</span>
                    <span>{formatPhoneNumber(phoneNumber)}</span>
                  </div>
                  <p className="ussd-hint">
                    Une demande de paiement a été envoyée sur votre téléphone.
                    Veuillez confirmer pour finaliser.
                  </p>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;